import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PriceCalculator from '../home/components/PriceCalculator';
import { useSEO } from '../../utils/seo';

export default function TaxiUtrechtRotterdamAirportPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useSEO({
    title: 'Taxi Utrecht Rotterdam Airport | 24/7 Airport Taxi',
    description: 'Taxi van Utrecht naar Rotterdam The Hague Airport. 24/7 beschikbaar, vaste prijs mogelijk en comfortabel vervoer.',
    keywords: 'taxi utrecht rotterdam airport, airport taxi utrecht, taxi utrecht rotterdam the hague airport, utrecht airport taxi',
    canonical: 'https://www.utrechtairporttaxi.nl/taxi-utrecht-rotterdam-the-hague-airport/'
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      question: 'Wat kost een taxi van Utrecht naar Rotterdam Airport?',
      answer: 'De prijs voor een taxi van Utrecht naar Rotterdam The Hague Airport is €77,50 all-in. Dit is een vaste prijs zonder verborgen kosten of nachttoeslagen.'
    },
    {
      question: 'Hoe lang duurt de rit naar Rotterdam Airport?',
      answer: 'De reistijd van Utrecht naar Rotterdam Airport is gemiddeld 45-55 minuten, afhankelijk van het verkeer en het tijdstip van de dag. Wij plannen altijd ruim zodat u op tijd aankomt.'
    },
    {
      question: 'Volgen jullie ook vluchten vanaf Rotterdam Airport?',
      answer: 'Ja, bij ophalen vanaf Rotterdam Airport volgen wij uw vlucht automatisch. Bij vertraging passen wij de ophaaltijd aan zonder extra kosten. Geef gewoon uw vluchtnummer door bij de reservering.'
    },
    {
      question: 'Zijn er nachttoeslagen voor ritten naar Rotterdam Airport?',
      answer: 'Nee, onze vaste prijzen gelden 24/7. Geen nachttoeslagen, geen weekendtoeslagen, geen verrassingen. De prijs die u vooraf ziet, is de prijs die u betaalt.'
    },
    {
      question: 'Kunnen jullie mij ophalen op Rotterdam Airport?',
      answer: 'Ja, wij verzorgen ook ritten vanaf Rotterdam The Hague Airport naar Utrecht. Onze chauffeur wacht op u in de aankomsthal met een naambord en helpt met uw bagage.'
    },
    {
      question: 'Hoeveel bagage kan er mee?',
      answer: 'In een standaard sedan is ruimte voor 3-4 koffers en handbagage. Heeft u meer bagage? Dan kunnen wij een stationwagon of grotere auto inzetten. Geef dit aan bij uw reservering.'
    },
    {
      question: 'Hoe kan ik reserveren?',
      answer: 'Reserveren kan via WhatsApp (06 5882 4429), telefonisch of via het online reserveringsformulier. U ontvangt direct een bevestiging met alle details.'
    },
    {
      question: 'Wat gebeurt er als mijn vlucht vertraging heeft?',
      answer: 'Bij ophalen vanaf Rotterdam Airport volgen wij uw vlucht automatisch. De chauffeur past de ophaaltijd aan zonder extra kosten. U hoeft ons niet te bellen.'
    },
    {
      question: 'Kan ik ook betalen met pin of creditcard?',
      answer: 'Ja, u kunt betalen met contant geld, pin, creditcard of vooraf via iDEAL. Kies zelf wat u het prettigst vindt.'
    },
    {
      question: 'Rijden jullie ook vanuit andere plaatsen naar Rotterdam Airport?',
      answer: 'Ja, wij rijden vanuit heel Utrecht en omgeving, inclusief Nieuwegein, Houten, Zeist, De Bilt, Maarssen en andere plaatsen in de regio.'
    }
  ];

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('calculator');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/373600b4d99aa9b8dee7f2d5ebbcf239.jpg')] bg-cover bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Taxi Utrecht – Rotterdam The Hague Airport
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              Zorgeloos vervoer van Utrecht naar Rotterdam Airport. Direct reserveren.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToBooking}
                className="px-6 sm:px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu & Zie Prijs
              </button>
              <a href="tel:+31658824429" className="px-6 sm:px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>Bel Direct
              </a>
              <a href="https://wa.me/31658824429" className="px-6 sm:px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>WhatsApp Reserveren
              </a>
            </div>
          </div>
        </div>
      </section>

      <div id="calculator">
        <PriceCalculator />
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Comfortabel Vervoer naar Rotterdam Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Met onze <strong>taxi van Utrecht naar Rotterdam The Hague Airport</strong> reis je comfortabel en zonder stress. Wij halen je op bij je woning, hotel of bedrijf en brengen je rechtstreeks naar de vertrekhal. Geen gedoe met parkeren, geen overstappen, gewoon ontspannen reizen.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze chauffeurs zijn ervaren en houden rekening met verkeersdrukte en vertrektijden. Voor veel ritten spreken we vooraf een <strong>vaste prijs</strong> af, zodat je nooit voor verrassingen komt te staan. De prijs die je vooraf ziet, is de prijs die je betaalt. Geen nachttoeslagen, geen weekendtoeslagen, geen verborgen kosten.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-8">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Vaste Prijs Garantie</h3>
              <p className="text-slate-700 mb-0">
                Taxi Utrecht naar Rotterdam Airport: <strong>€77,50 all-in</strong><br />
                24/7 dezelfde prijs • Geen nachttoeslagen • Inclusief vluchttracking
              </p>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Waarom Kiezen voor Utrecht Airport Taxi?</h2>
            
            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-24-hours-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">24/7 Beschikbaar</h3>
                <p className="text-slate-700">Ook voor vroege ochtendvluchten en late aankomsten. Wij rijden altijd.</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-price-tag-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Vaste Prijs Mogelijk</h3>
                <p className="text-slate-700">Duidelijke afspraken vooraf. Geen onverwachte kosten achteraf.</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-route-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Geen Overstappen</h3>
                <p className="text-slate-700">Direct van deur tot deur. Geen gedoe met openbaar vervoer of parkeren.</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-luggage-cart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Bagage Inbegrepen</h3>
                <p className="text-slate-700">Ruimte voor koffers en handbagage. Wij helpen met in- en uitladen.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Over Rotterdam The Hague Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Rotterdam The Hague Airport is een compact en overzichtelijk vliegveld, ideaal voor Europese bestemmingen. Het vliegveld is minder druk dan Schiphol, waardoor inchecken en security vaak sneller gaan. Vanuit Utrecht is Rotterdam Airport goed bereikbaar via de A12 en A20.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              De reistijd van Utrecht naar Rotterdam Airport is gemiddeld 45-55 minuten, afhankelijk van het verkeer en het tijdstip van de dag. Onze chauffeurs kennen de beste routes en zorgen dat je op tijd aankomt voor je vlucht. Wij adviseren om minimaal 2 uur voor vertrek te vertrekken voor Europese vluchten.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Ook Ophalen vanaf Rotterdam Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Kom je aan op Rotterdam Airport en heb je een <strong>taxi naar Utrecht</strong> nodig? Wij verzorgen ook ritten vanaf het vliegveld naar Utrecht en omgeving. Onze chauffeur wacht op je in de aankomsthal met een naambord, helpt met je bagage en brengt je comfortabel naar huis.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Dankzij ons vluchttracking systeem volgen wij je vlucht in real-time. Heeft je vlucht vertraging? Geen probleem. Wij passen de ophaaltijd automatisch aan zonder extra kosten. Je hoeft ons niet te bellen – wij regelen het voor je.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Lokale Service vanuit Utrecht</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Wij rijden vanuit heel Utrecht en omgeving, inclusief Leidsche Rijn, Lombok, Oog in Al, Overvecht, Kanaleneiland en het centrum van Utrecht. Ook vanuit Nieuwegein, Houten, Zeist, De Bilt, Maarssen en andere plaatsen in de regio verzorgen wij ritten naar Rotterdam Airport.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Als lokale taxiservice kennen wij de regio door en door. Onze chauffeurs wonen en werken in Utrecht, waardoor wij altijd snel ter plaatse kunnen zijn. Geen landelijke taxicentrale, maar een lokaal bedrijf dat dicht bij je staat.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Hoe Werkt Reserveren?</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Reserveren is eenvoudig en snel. Je kunt op drie manieren contact met ons opnemen:
            </p>

            <ul className="list-disc pl-6 mb-6 text-lg text-slate-700 space-y-2">
              <li><strong>WhatsApp:</strong> Stuur een bericht naar 06 5882 4429 met je ritgegevens</li>
              <li><strong>Telefonisch:</strong> Bel 06 5882 4429 (24/7 bereikbaar)</li>
              <li><strong>Online:</strong> Vul het reserveringsformulier in op deze pagina</li>
            </ul>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Geef bij je reservering de volgende gegevens door: ophaaladres, datum, tijd, bestemming (Rotterdam Airport), aantal passagiers en vluchtnummer (indien van toepassing). Je ontvangt direct een bevestiging met alle details en de totaalprijs.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Direct Reserveren?</h3>
              <p className="text-lg text-slate-700 mb-6">
                Bereken je prijs en boek direct online. Of neem contact met ons op voor persoonlijk advies.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={scrollToBooking}
                  className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu
                </button>
                <a href="tel:+31658824429" className="px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>06 5882 4429
                </a>
                <a href="https://wa.me/31658824429" className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line mr-2"></i>WhatsApp
                </a>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Andere Airport Taxi Routes</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Naast Rotterdam Airport verzorgen wij ook ritten naar andere luchthavens:
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-8">
              <a href="/taxi-utrecht-schiphol" className="block bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-amber-500 transition-colors">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Taxi Utrecht – Schiphol</h3>
                <p className="text-slate-600 mb-0">De meest populaire route. Vaste prijs €65,00.</p>
              </a>

              <a href="/taxi-utrecht-eindhoven-airport" className="block bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-amber-500 transition-colors">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Taxi Utrecht – Eindhoven Airport</h3>
                <p className="text-slate-600 mb-0">Comfortabel naar Eindhoven Airport.</p>
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 text-center">Veelgestelde Vragen</h2>
          <p className="text-xl text-slate-600 text-center mb-12">Alles over onze taxi service naar Rotterdam Airport</p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <summary className="text-lg font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl text-amber-500"></i>
                </summary>
                <p className="mt-4 text-slate-700 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
